export const ThemeEvents = {
  cartUpdate: 'cart:update',
  cartError: 'cart:error',
  heroTone: 'hero:tone-change',
  navigationStart: 'site:navigation:start',
  navigationCommit: 'site:navigation:commit',
  navigationEnd: 'site:navigation:end',
  navigationError: 'site:navigation:error',
  navigationFallback: 'site:navigation:fallback',
};

/**
 * Event dispatched after items are added to the cart.
 * Uses the same event name as CartUpdateEvent so all cart surfaces
 * react to both additions and quantity changes uniformly.
 */
export class CartAddEvent extends CustomEvent {
  static eventName = ThemeEvents.cartUpdate;

  /**
   * @param {Object} [resource] - The cart object from the API response
   * @param {string} [sourceId] - The ID of the element that triggered the action
   * @param {Object} [data] - Additional event data
   * @param {number} [data.itemCount] - Number of items added
   * @param {string} [data.source] - Source of the cart update (e.g. 'product-form-component')
   * @param {string} [data.productId] - The product ID
   * @param {Record<string, string>} [data.sections] - Section HTML keyed by section ID
   * @param {boolean} [data.didError] - Whether the cart operation failed
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, {
      bubbles: true,
      detail: {
        resource,
        sourceId,
        data: { ...data },
      },
    });
  }
}

/**
 * Event dispatched after cart quantities are updated or items removed.
 */
export class CartUpdateEvent extends CustomEvent {
  /**
   * @param {Object} resource - The cart object from the API response
   * @param {string} sourceId - The ID of the element that triggered the action
   * @param {Object} [data] - Additional event data
   * @param {number} [data.itemCount] - New total item count
   * @param {string} [data.source] - Source of the cart update
   * @param {Record<string, string>} [data.sections] - Section HTML keyed by section ID
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, {
      bubbles: true,
      detail: {
        resource,
        sourceId,
        data: { ...data },
      },
    });
  }
}

/**
 * Event dispatched when a cart operation fails.
 */
export class CartErrorEvent extends CustomEvent {
  /**
   * @param {string} sourceId - The ID of the element that triggered the action
   * @param {string} message - Error message from the server
   * @param {Object} [description] - Description from the server
   * @param {Object} [errors] - Errors from the server
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, {
      bubbles: true,
      detail: {
        sourceId,
        data: {
          message,
          errors,
          description,
        },
      },
    });
  }
}
